import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Input,
  Output,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UiButtonComponent } from '@template-ui/components';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '@app/core/services/auth.service';
import { RoleService } from '@app/shared/services/roles.service';
import {
  WorkflowDefinitionActivationDetail,
  WorkflowDefinitionDraftDetail,
  WorkflowDefinitionDraftValidationResult,
  WorkflowDefinitionLifecycleDetail,
  WorkflowDefinitionVersionChangePreview,
} from './platform-workflow-definition-lifecycle.models';
import {
  PlatformWorkflowDefinitionLifecycleService,
  WorkflowDefinitionMutationIntent,
  createWorkflowDefinitionMutationIntent,
} from './platform-workflow-definition-lifecycle.service';
import type {
  WorkflowDefinitionVersionDetail,
  WorkflowRegistryDiagnosticDisplayError,
} from './platform-workflow-registry-diagnostics.models';
import { mapWorkflowRegistryDiagnosticError } from './platform-workflow-registry-diagnostics.models';
import {
  WorkflowDraftConfirmationRequest,
  WorkflowDraftConfirmationService,
} from './workflow-draft-confirmation.service';
import {
  workflowDraftExecutionSupport,
  workflowDraftPublicationReadiness,
} from './workflow-publication-readiness';
import { workflowActivationSchedule } from './workflow-activation-schedule';
import { workflowVersionReviewPresentation } from './workflow-version-presentation';

const PUBLISH_PERMISSION = 'workflow_definition.publish';
const ACTIVATE_PERMISSION = 'workflow_definition.activate';
const RETIRE_PERMISSION = 'workflow_definition.retire';
const ROLLBACK_PERMISSION = 'workflow_definition.rollback';
const PENDING_OPERATION_STORAGE_KEY =
  'workflow-registry.pending-assurance-operation';
const PENDING_OPERATION_LIFETIME_MS = 10 * 60 * 1000;

export function clearArchivedDraftPublicationIntent(draftId: number): void {
  try {
    const raw = window.sessionStorage.getItem(PENDING_OPERATION_STORAGE_KEY);
    const pending = raw
      ? (JSON.parse(raw) as { kind?: string; targetId?: number })
      : null;
    if (pending?.kind === 'publish' && pending.targetId === draftId)
      window.sessionStorage.removeItem(PENDING_OPERATION_STORAGE_KEY);
  } catch {
    /* Storage may be unavailable; archived publication remains blocked by readiness. */
  }
}

type LifecycleActionKind =
  | 'publish'
  | 'activate'
  | 'retire'
  | 'rollback'
  | 'make_current';
type ScheduleField =
  | 'activationStart'
  | 'activationEnd'
  | 'rollbackStart'
  | 'rollbackEnd';

export interface WorkflowVersionActionView {
  readonly context: string;
  readonly generation: number;
}
interface OwnedVersionAction {
  readonly view: WorkflowVersionActionView;
  readonly actor: string;
  readonly permission: 'activate' | 'rollback' | 'retire';
  readonly background: HTMLElement | null;
}
interface PendingLifecycleOperation {
  kind: LifecycleActionKind;
  actorId: string;
  targetId: number;
  targetETag: string | null;
  idempotencyKey: string;
  values: Record<string, string | null>;
  expiresUtc: string;
  sourceContext?: string;
}

@Component({
  selector: 'phms-workflow-definition-lifecycle-actions',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    CommonModule,
    FormsModule,
    UiButtonComponent,
  ],
  templateUrl: './workflow-definition-lifecycle-actions.component.html',
  styleUrl: './workflow-definition-lifecycle-actions.component.scss',
})
export class WorkflowDefinitionLifecycleActionsComponent implements OnChanges {
  private readonly lifecycle = inject(
    PlatformWorkflowDefinitionLifecycleService,
  );
  private readonly roles = inject(RoleService);
  private readonly auth = inject(AuthService);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly dialogs = inject(MatDialog);
  private versionGeneration = 0;
  private managedReadContext = '';
  private managedSourceContext = '';
  private mutationRequest = 0;

  @Input() managedVersionActions = false;
  @Input() versionSourceContext = '';
  @Input() versionOwnerContext = '';
  @Input() currentVersionOwnerContext: (() => string | null) | null = null;
  @Input() currentVersionSourceContext: (() => string | null) | null = null;

  private versionState: WorkflowDefinitionVersionDetail | null = null;
  private draftState: WorkflowDefinitionDraftDetail | null = null;
  private actionIntent: WorkflowDefinitionMutationIntent | null = null;
  private managedIntentKey: string | null = null;
  private activationRequestId = 0;
  private activationHistoryError: {
    source: string;
    title: string | null;
    messages: string[];
    reference: string | null;
  } | null = null;
  private versionChangeRequestId = 0;
  private restoredInstants: Partial<
    Record<ScheduleField, { local: string; utc: string }>
  > = {};

  @Input()
  set draft(value: WorkflowDefinitionDraftDetail | null) {
    const changed = this.draftState?.id !== value?.id;
    this.draftState = value;
    if (value?.lifecycleStatusCode === 'archived') {
      this.actionIntent = null;
      clearArchivedDraftPublicationIntent(value.id);
    }
    if (changed) {
      this.publishDisplayName = this.publishReasonCode = '';
      this.actionIntent = null;
      this.clearMessages();
    }
  }

  get draft(): WorkflowDefinitionDraftDetail | null {
    return this.draftState;
  }

  @Input()
  set version(value: WorkflowDefinitionVersionDetail | null) {
    const changed = this.versionState?.id !== value?.id;
    this.versionState = value;
    if (changed) {
      this.versionAction.set(null);
      this.retiringActivationId.set(null);
      this.activationStart =
        this.activationEnd =
        this.activationReasonCode =
          '';
      this.rollbackStart = this.rollbackEnd = this.rollbackReasonCode = '';
      this.retireReasonCodes = {};
      this.restoredInstants = {};
      this.actionIntent = null;
      this.clearMessages();
      this.activations.set([]);
      this.versionChange.set(null);
      this.activationTotalCount.set(0);
      if (!this.managedVersionActions) {
        this.loadVersionChangePreview();
        this.loadActivations();
      }
    }
  }

  get version(): WorkflowDefinitionVersionDetail | null {
    return this.versionState;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.managedVersionActions) return;
    const context = this.versionKey();
    if (
      context !== this.managedReadContext ||
      changes['interactionBlocked'] ||
      changes['managedVersionActions']
    ) {
      const source = this.versionSourceKey();
      const changedSource = source !== this.managedSourceContext;
      this.managedSourceContext = source;
      this.managedReadContext = context;
      this.versionGeneration++;
      this.activationRequestId++;
      this.versionChangeRequestId++;
      this.isLoadingActivations.set(false);
      this.isLoadingVersionChange.set(false);
      if (changedSource) {
        this.actionIntent = null;
        this.managedIntentKey = null;
        this.clearMessages();
        this.versionChangeFailed.set(false);
        this.activationsLoadFailed.set(false);
        this.versionChange.set(null);
        this.activations.set([]);
        this.activationTotalCount.set(0);
      }
      const view = this.versionView;
      if (this.versionCurrent(view)) {
        this.loadVersionChangePreview();
        this.loadActivations();
        const assurance = this.auth.assuranceStepUpResult();
        if (assurance) this.restorePendingOperation(assurance);
      } else {
        this.versionChangeFailed.set(true);
        this.activationsLoadFailed.set(true);
      }
    }
  }

  get versionView(): WorkflowVersionActionView | null {
    return this.version
      ? { context: this.versionKey(), generation: this.versionGeneration }
      : null;
  }
  get versionReview() {
    const preview = this.versionChange();
    return this.version
      ? workflowVersionReviewPresentation(
          this.version,
          preview?.definitionVersionId === this.version.id &&
            !this.versionChangeFailed() &&
            !this.isLoadingVersionChange()
            ? preview
            : null,
        )
      : null;
  }
  @Input() hasUnsavedChanges = false;
  @Input() interactionBlocked = false;
  @Input() validationFailed = false;
  @Input() showPublicationReadiness = true;
  /** The owning readiness summary renders the single Publish action. */
  @Input() publicationLayout: 'standalone' | 'embedded' = 'standalone';

  @Output() readonly published =
    new EventEmitter<WorkflowDefinitionLifecycleDetail>();
  @Output() readonly versionChanged = new EventEmitter<void>();
  @Output() readonly operationInProgressChange = new EventEmitter<boolean>();
  @Output() readonly draftValidated =
    new EventEmitter<WorkflowDefinitionDraftValidationResult>();
  @Output() readonly publicationChecksFailed =
    new EventEmitter<WorkflowRegistryDiagnosticDisplayError>();

  readonly canPublish = this.roles.hasAnyPermissionSignal(PUBLISH_PERMISSION);
  readonly canValidate = this.roles.hasAnyPermissionSignal(
    'workflow_definition.validate',
  );
  readonly canActivate = this.roles.hasAnyPermissionSignal(ACTIVATE_PERMISSION);
  readonly canRetire = this.roles.hasAnyPermissionSignal(RETIRE_PERMISSION);
  readonly canRollback = this.roles.hasAnyPermissionSignal(ROLLBACK_PERMISSION);
  readonly isBusy = signal(false);
  readonly isConfirming = signal(false);
  readonly isCheckingPublication = signal(false);
  readonly isLoadingActivations = signal(false);
  readonly activationsLoadFailed = signal(false);
  readonly activationTotalCount = signal(0);
  readonly versionAction = signal<'activate' | 'rollback' | null>(null);
  readonly retiringActivationId = signal<number | null>(null);
  readonly localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  readonly activations = signal<WorkflowDefinitionActivationDetail[]>([]);
  readonly errorTitle = signal<string | null>(null);
  readonly errorMessages = signal<string[]>([]);
  readonly errorReference = signal<string | null>(null);
  readonly statusMessage = signal<string | null>(null);
  readonly versionChange =
    signal<WorkflowDefinitionVersionChangePreview | null>(null);
  readonly isLoadingVersionChange = signal(false);
  readonly versionChangeFailed = signal(false);

  publishDisplayName = '';
  publishReasonCode = '';
  activationStart = '';
  activationEnd = '';
  activationReasonCode = '';
  rollbackStart = '';
  rollbackEnd = '';
  rollbackReasonCode = '';
  retireReasonCodes: Record<number, string> = {};

  constructor() {
    effect(() => this.operationInProgressChange.emit(this.isBusy()));
    effect(() => {
      const result = this.auth.assuranceStepUpResult();
      if (result) {
        this.restorePendingOperation(result);
      }
    });
  }

  hasAnyVersionPermission(): boolean {
    return this.canActivate() || this.canRetire() || this.canRollback();
  }

  selectVersionAction(
    action: 'activate' | 'rollback',
    view?: WorkflowVersionActionView | null,
  ): void {
    if (!this.versionCurrent(view)) return;
    if (
      this.isBusy() ||
      this.isConfirming() ||
      this.interactionBlocked ||
      (action === 'activate' ? !this.canActivate() : !this.canRollback())
    )
      return;
    this.versionAction.set(this.versionAction() === action ? null : action);
  }

  selectRetirement(
    activation: WorkflowDefinitionActivationDetail,
    view?: WorkflowVersionActionView | null,
  ): void {
    if (
      !this.versionCurrent(view) ||
      (this.managedVersionActions && !this.currentActivation(activation))
    )
      return;
    if (
      this.isBusy() ||
      this.isConfirming() ||
      this.interactionBlocked ||
      !this.canRetire() ||
      activation.lifecycleStatusCode === 'retired'
    )
      return;
    this.retiringActivationId.set(
      this.retiringActivationId() === activation.id ? null : activation.id,
    );
  }
  activationSchedule() {
    return workflowActivationSchedule(
      this.activationStart,
      this.activationEnd,
      true,
      {
        startUtc: this.restoredInstant('activationStart'),
        endUtc: this.restoredInstant('activationEnd'),
      },
    );
  }

  rollbackSchedule() {
    return workflowActivationSchedule(
      this.rollbackStart,
      this.rollbackEnd,
      false,
      {
        startUtc: this.restoredInstant('rollbackStart'),
        endUtc: this.restoredInstant('rollbackEnd'),
      },
    );
  }

  schedulePreview(action: 'activate' | 'rollback'): string {
    const schedule =
      action === 'activate'
        ? this.activationSchedule()
        : this.rollbackSchedule();
    if (!schedule.valid)
      return 'Correct the schedule before reviewing this action.';
    const start = schedule.startUtc
      ? `From ${this.formatDateTime(schedule.startUtc)}`
      : 'From the time the server processes the rollback';
    return `${start}, ${schedule.endUtc ? `until ${this.formatDateTime(schedule.endUtc)} (end excluded)` : 'with no scheduled end'}.`;
  }

  versionActionContext(): string {
    const version = this.version;
    if (!version) return 'No version selected.';
    return `${this.versionName()}, version ${version.definitionVersionCode}.`;
  }

  publicationName(): string {
    return (
      this.publishDisplayName.trim() ||
      this.draft?.document?.displayName?.trim() ||
      'this workflow'
    );
  }

  versionName(): string {
    return (
      this.version?.displayName?.trim() ||
      this.version?.definitionDocument?.displayName?.trim() ||
      'this workflow'
    );
  }

  activationPostureLabel(
    activation: WorkflowDefinitionActivationDetail,
  ): string {
    return (
      (
        {
          active: 'Active window',
          scheduled: 'Scheduled window',
          expired: 'Ended window',
          retired: 'Retired window',
          not_activated: 'Not activated',
        } as Record<string, string>
      )[activation.activationPostureCode] ??
      `Reported status: ${activation.activationPostureCode || 'not recorded'}`
    );
  }

  retryActivationHistory(view?: WorkflowVersionActionView | null): void {
    if (!this.versionCurrent(view)) return;
    if (!this.isBusy() && !this.isLoadingActivations()) this.loadActivations();
  }

  publicationReadiness() {
    return workflowDraftPublicationReadiness(this.draft, {
      hasUnsavedChanges: this.hasUnsavedChanges,
      canPublish: this.canPublish(),
      actorUserId: this.roles.accessProfile?.()?.actor.userId,
      interactionBlocked: this.interactionBlocked || this.isBusy(),
      validationFailed: this.validationFailed,
    });
  }

  draftExecutionSupport() {
    return workflowDraftExecutionSupport(this.draft);
  }

  publishBlockedReason(): string | null {
    return this.publicationReadiness().blockedReason;
  }

  showPublicationForm(): boolean {
    return (
      this.draft?.lifecycleStatusCode !== 'archived' &&
      this.canPublish() &&
      (this.publishBlockedReason() === null || this.isBusy())
    );
  }

  canSubmitPublish(): boolean {
    return (
      !this.destroyRef.destroyed &&
      this.canPublish() &&
      !this.interactionBlocked &&
      !this.isBusy() &&
      !this.isConfirming() &&
      !this.isCheckingPublication() &&
      this.publishReasonCode.trim().length <= 120 &&
      this.publishDisplayName.trim().length <= 150 &&
      this.publishBlockedReason() === null
    );
  }

  publishActionLabel(): string {
    return this.isCheckingPublication()
      ? 'Checking…'
      : this.isBusy()
        ? 'Publishing…'
        : 'Publish';
  }

  canSubmitActivation(): boolean {
    return (
      this.canActivate() &&
      !this.interactionBlocked &&
      !this.isBusy() &&
      !this.isConfirming() &&
      this.activationSchedule().valid &&
      this.activationReasonCode.trim().length <= 120
    );
  }

  canActivateNow(): boolean {
    return (
      !!this.version &&
      this.version.isExecutable !== true &&
      !this.isLoadingVersionChange() &&
      !this.versionChangeFailed() &&
      this.versionChange()?.definitionVersionId === this.version.id &&
      this.versionChange()?.actionCode === 'activate' &&
      this.canActivate() &&
      !this.interactionBlocked &&
      !this.isBusy() &&
      !this.isConfirming() &&
      this.activationReasonCode.trim().length <= 120
    );
  }

  canMakeCurrent(): boolean {
    const preview = this.versionChange();
    return (
      !!this.version &&
      preview?.definitionVersionId === this.version.id &&
      !this.destroyRef.destroyed &&
      preview.actionCode === 'make_current' &&
      !!preview.currentActivationId &&
      !!preview.currentActivationETag &&
      this.canActivate() &&
      !this.interactionBlocked &&
      !this.isBusy() &&
      !this.isConfirming() &&
      !this.isLoadingVersionChange() &&
      !this.versionChangeFailed() &&
      this.activationReasonCode.trim().length <= 120
    );
  }

  retryVersionChangePreview(view?: WorkflowVersionActionView | null): void {
    if (!this.versionCurrent(view)) return;
    if (!this.isBusy() && !this.isConfirming()) this.loadVersionChangePreview();
  }

  async makeCurrentVersion(
    view?: WorkflowVersionActionView | null,
  ): Promise<void> {
    if (!this.versionCurrent(view)) return;
    const owner = this.captureVersionAction(view, 'activate');
    const version = this.version;
    const preview = this.versionChange();
    if (!version || !preview || !this.canMakeCurrent()) return;
    const command = {
      expectedActivationId: preview.currentActivationId!,
      expectedActivationETag: preview.currentActivationETag!,
      reasonCode: this.note(
        this.activationReasonCode,
        'New workflow version made current.',
      ),
    };
    if (
      !(await this.confirmAction({
        heading: `Make ${this.versionName()} current?`,
        tone: 'info',
        message: `New runs will use version ${version.definitionVersionCode}, replacing version ${preview.currentDefinitionVersionCode}. Existing runs keep their current version.${preview.effectiveEndUtc ? ` This change lasts until ${this.formatDateTime(preview.effectiveEndUtc)}; the later schedule stays in place.` : ''}`,
        confirmText: 'Make current version',
        cancelText: 'Keep reviewing',
      }))
    )
      return;
    if (
      !this.versionActionCurrent(owner) ||
      this.version?.id !== version.id ||
      this.versionChange() !== preview ||
      !this.canMakeCurrent() ||
      command.reasonCode !==
        this.note(
          this.activationReasonCode,
          'New workflow version made current.',
        )
    )
      return;
    await this.runAction(
      'make_current',
      version.id,
      null,
      {
        expectedActivationId: String(command.expectedActivationId),
        expectedActivationETag: command.expectedActivationETag,
        reasonCode: command.reasonCode,
      },
      () =>
        firstValueFrom(
          this.lifecycle.makeCurrentVersion(version.id, command, this.intent()),
        ),
      (result) =>
        this.onActivationSucceeded(
          result,
          'New runs now use this workflow version. Existing runs keep their version.',
        ),
      owner,
    );
  }

  canSubmitRollback(): boolean {
    return (
      this.canRollback() &&
      !this.interactionBlocked &&
      !this.isBusy() &&
      !this.isConfirming() &&
      this.rollbackSchedule().valid &&
      this.rollbackReasonCode.trim().length <= 120
    );
  }
  async publishDraft(): Promise<void> {
    const draft = this.draft;
    if (!draft || !this.canSubmitPublish()) {
      return;
    }
    const values = this.publicationValues();
    const revision = draft.revision;
    const eTag = draft.eTag;
    if (this.canValidate()) {
      this.isCheckingPublication.set(true);
      this.clearMessages();
      try {
        const checked = await firstValueFrom(
          this.lifecycle
            .validateDraft(
              draft.id,
              eTag,
              createWorkflowDefinitionMutationIntent(),
            )
            .pipe(takeUntilDestroyed(this.destroyRef)),
        );
        if (
          this.destroyRef.destroyed ||
          this.draft?.id !== draft.id ||
          this.draft?.revision !== revision ||
          this.draft?.eTag !== eTag ||
          !this.canValidate() ||
          !this.canPublish()
        )
          return;
        if (
          checked.draftId !== draft.id ||
          checked.revision !== revision ||
          checked.eTag !== eTag
        ) {
          this.errorTitle.set('Saved revision changed');
          this.errorMessages.set([
            'Reload the latest saved draft before publishing. Its checks no longer match this revision.',
          ]);
          this.publicationChecksFailed.emit({
            title: 'Saved revision changed',
            messages: this.errorMessages(),
            statusCode: 412,
          });
          return;
        }
        this.draft = {
          ...draft,
          validationStatusCode: checked.validationStatusCode,
          validationDiagnostics: checked.diagnostics,
          compilerVersion: checked.compilerVersion,
          canonicalContentHash: checked.canonicalContentHash,
          runtimeBindingCode: checked.runtimeBindingCode,
          isExecutable: checked.isExecutable,
        };
        this.draftValidated.emit(checked);
        if (checked.validationStatusCode !== 'valid') {
          this.errorTitle.set('Saved revision needs changes');
          this.errorMessages.set(
            checked.diagnostics.length
              ? checked.diagnostics.map((finding) => finding.message)
              : [
                  'This revision did not pass its publication checks. Review the findings before publishing.',
                ],
          );
          return;
        }
      } catch (error) {
        if (
          this.destroyRef.destroyed ||
          this.draft?.id !== draft.id ||
          this.draft?.revision !== revision ||
          this.draft?.eTag !== eTag
        )
          return;
        const displayError = mapWorkflowRegistryDiagnosticError(error);
        this.errorTitle.set('Publication checks could not finish');
        this.errorMessages.set(displayError.messages);
        this.errorReference.set(displayError.correlationId || null);
        this.publicationChecksFailed.emit({
          ...displayError,
          title: 'Publication checks could not finish',
        });
        return;
      } finally {
        this.isCheckingPublication.set(false);
      }
    }
    if (
      this.draft?.id !== draft.id ||
      this.draft?.revision !== revision ||
      this.draft?.eTag !== eTag ||
      !this.canSubmitPublish() ||
      !this.sameValues(values, this.publicationValues())
    )
      return;
    if (
      !(await this.confirmAction({
        heading: `Publish ${this.publicationName()}?`,
        tone: 'info',
        message: `Save revision ${revision} as version ${draft.document.definitionVersionCode}. To make changes later, create a new draft. After publishing, activate this version to allow new process runs. Existing runs keep their current version.`,
        confirmText: 'Publish',
        cancelText: 'Keep reviewing',
      }))
    ) {
      return;
    }

    if (
      this.draft?.id !== draft.id ||
      this.draft?.revision !== revision ||
      this.draft?.eTag !== eTag ||
      !this.canSubmitPublish() ||
      !this.sameValues(values, this.publicationValues())
    )
      return;
    await this.runAction(
      'publish',
      draft.id,
      draft.eTag,
      values,
      () =>
        firstValueFrom(
          this.lifecycle.publishDraft(
            draft.id,
            draft.eTag,
            values,
            this.intent(),
          ),
        ),
      (result) => {
        this.statusMessage.set(
          `Published version ${result.definitionVersionCode}. Next, activate it for new process runs.`,
        );
        this.published.emit(result);
      },
    );
  }

  async activateVersion(
    form?: HTMLFormElement,
    view?: WorkflowVersionActionView | null,
  ): Promise<void> {
    if (
      !this.versionCurrent(view) ||
      !this.versionFormCurrent(form, 'activate')
    )
      return;
    const owner = this.captureVersionAction(view, 'activate');
    const version = this.version;
    if (
      !version ||
      !this.canSubmitActivation() ||
      (form && !form.reportValidity())
    ) {
      return;
    }
    const selectedAction = this.versionAction();
    const values = {
      effectiveStartUtc: this.activationSchedule().startUtc!,
      effectiveEndUtc: this.activationSchedule().endUtc,
      reasonCode: this.note(
        this.activationReasonCode,
        'Scheduled activation from workflow editor.',
      ),
    };
    if (
      !values.effectiveStartUtc ||
      !(await this.confirmAction({
        heading: `Schedule ${this.versionName()}?`,
        tone: 'info',
        message: `${this.versionActionContext()} ${this.schedulePreview('activate')} The server checks whether this version can be used for new process runs. Existing runs keep their current version.`,
        confirmText: 'Schedule activation',
        cancelText: 'Keep reviewing',
      }))
    ) {
      return;
    }
    if (
      !this.versionActionCurrent(owner) ||
      !this.versionFormCurrent(form, 'activate', undefined, owner) ||
      this.version?.id !== version.id ||
      this.versionAction() !== selectedAction ||
      !this.canSubmitActivation() ||
      !this.sameValues(values, {
        effectiveStartUtc: this.activationSchedule().startUtc,
        effectiveEndUtc: this.activationSchedule().endUtc,
        reasonCode: this.note(
          this.activationReasonCode,
          'Scheduled activation from workflow editor.',
        ),
      })
    )
      return;

    await this.runAction(
      'activate',
      version.id,
      null,
      values,
      () =>
        firstValueFrom(
          this.lifecycle.activateVersion(version.id, values, this.intent()),
        ),
      (result) =>
        this.onActivationSucceeded(result, 'Activation schedule saved.'),
      owner,
    );
  }

  async activateNow(view?: WorkflowVersionActionView | null): Promise<void> {
    if (!this.versionCurrent(view)) return;
    const owner = this.captureVersionAction(view, 'activate');
    const version = this.version;
    if (!version || !this.canActivateNow()) return;
    const selectedAction = this.versionAction();
    const enteredStart = this.activationStart;
    const enteredEnd = this.activationEnd;
    const values = {
      effectiveStartUtc: null,
      effectiveEndUtc: null,
      reasonCode: this.note(
        this.activationReasonCode,
        'Activated for new process runs.',
      ),
    };
    if (
      !(await this.confirmAction({
        heading: `Activate ${this.versionName()} now?`,
        tone: 'info',
        message: `Use version ${version.definitionVersionCode} for new process runs, starting now. The server checks availability before saving. Existing runs keep their current version.`,
        confirmText: 'Activate now',
        cancelText: 'Keep reviewing',
      }))
    )
      return;
    if (
      !this.versionActionCurrent(owner) ||
      this.version?.id !== version.id ||
      !this.canActivateNow() ||
      this.versionAction() !== selectedAction ||
      this.activationStart !== enteredStart ||
      this.activationEnd !== enteredEnd ||
      values.reasonCode !==
        this.note(this.activationReasonCode, 'Activated for new process runs.')
    )
      return;
    await this.runAction(
      'activate',
      version.id,
      null,
      values,
      () =>
        firstValueFrom(
          this.lifecycle.activateVersion(version.id, values, this.intent()),
        ),
      (result) =>
        this.onActivationSucceeded(
          result,
          'Activation saved. Check this version’s availability for new process runs.',
        ),
      owner,
    );
  }

  async retireActivation(
    activation: WorkflowDefinitionActivationDetail,
    view?: WorkflowVersionActionView | null,
    form?: HTMLFormElement,
  ): Promise<void> {
    if (
      !this.versionCurrent(view) ||
      !this.versionFormCurrent(form, 'retire', activation) ||
      (this.managedVersionActions && !this.currentActivation(activation))
    )
      return;
    const owner = this.captureVersionAction(view, 'retire');
    const enteredNote = this.retireReasonCodes[activation.id] ?? '';
    const reasonCode = this.note(
      enteredNote,
      'Activation retired from workflow editor.',
    );
    if (
      !this.version ||
      activation.workflowDefinitionVersionId !== this.version.id ||
      !this.canRetire() ||
      this.interactionBlocked ||
      this.isBusy() ||
      this.isConfirming() ||
      enteredNote.trim().length > 120 ||
      activation.lifecycleStatusCode === 'retired'
    ) {
      return;
    }
    const eTag = activation.eTag;
    if (
      !(await this.confirmAction({
        heading: 'Retire activation?',
        message: `${this.versionActionContext()} Retire only the activation from ${this.formatDateTime(activation.effectiveStartUtc)} ${activation.effectiveEndUtc ? `until ${this.formatDateTime(activation.effectiveEndUtc)}` : 'with no scheduled end'}. This removes this window from runtime selection. Other activation windows are unchanged. Existing workflow instances keep their version; the immutable document is unchanged.`,
        confirmText: 'Retire activation',
        cancelText: 'Keep activation',
      }))
    ) {
      return;
    }
    if (
      !this.versionActionCurrent(owner) ||
      !this.versionFormCurrent(form, 'retire', activation, owner) ||
      this.version?.id !== activation.workflowDefinitionVersionId ||
      !this.canRetire() ||
      this.interactionBlocked ||
      this.isBusy() ||
      (this.managedVersionActions && !this.currentActivation(activation)) ||
      activation.eTag !== eTag ||
      activation.lifecycleStatusCode === 'retired' ||
      reasonCode !==
        this.note(
          this.retireReasonCodes[activation.id] ?? '',
          'Activation retired from workflow editor.',
        )
    )
      return;

    await this.runAction(
      'retire',
      activation.id,
      activation.eTag,
      { reasonCode },
      () =>
        firstValueFrom(
          this.lifecycle.retireActivation(
            activation.id,
            activation.eTag,
            { reasonCode },
            this.intent(),
          ),
        ),
      () => this.onVersionMutationSucceeded('Activation retired.'),
      owner,
    );
  }

  async rollbackToVersion(
    form?: HTMLFormElement,
    view?: WorkflowVersionActionView | null,
  ): Promise<void> {
    if (
      !this.versionCurrent(view) ||
      !this.versionFormCurrent(form, 'rollback')
    )
      return;
    const owner = this.captureVersionAction(view, 'rollback');
    const version = this.version;
    if (
      !version ||
      !this.canSubmitRollback() ||
      (form && !form.reportValidity())
    ) {
      return;
    }
    const selectedAction = this.versionAction();
    const values = {
      effectiveStartUtc: this.rollbackSchedule().startUtc,
      effectiveEndUtc: this.rollbackSchedule().endUtc,
      reasonCode: this.note(
        this.rollbackReasonCode,
        'Rollback requested from workflow editor.',
      ),
    };
    if (
      !(await this.confirmAction({
        heading: 'Roll back to this version?',
        message: `${this.versionActionContext()} ${this.schedulePreview('rollback')} Request this version in place of the existing selection for the requested window. The server checks coverage, conflicts and runtime support. Existing workflow instances keep their version.`,
        confirmText: 'Create rollback',
        cancelText: 'Keep reviewing',
      }))
    ) {
      return;
    }
    if (
      !this.versionActionCurrent(owner) ||
      !this.versionFormCurrent(form, 'rollback', undefined, owner) ||
      this.version?.id !== version.id ||
      this.versionAction() !== selectedAction ||
      !this.canSubmitRollback() ||
      !this.sameValues(values, {
        effectiveStartUtc: this.rollbackSchedule().startUtc,
        effectiveEndUtc: this.rollbackSchedule().endUtc,
        reasonCode: this.note(
          this.rollbackReasonCode,
          'Rollback requested from workflow editor.',
        ),
      })
    )
      return;

    await this.runAction(
      'rollback',
      version.id,
      null,
      values,
      () =>
        firstValueFrom(
          this.lifecycle.rollbackToVersion(version.id, values, this.intent()),
        ),
      () => this.onVersionMutationSucceeded('Rollback activation saved.'),
      owner,
    );
  }
  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'Not recorded';
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? value
      : new Intl.DateTimeFormat(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'shortOffset',
        }).format(parsed);
  }

  private actorKey(): string {
    return JSON.stringify(this.roles.accessProfile()?.actor ?? null);
  }
  private versionSourceKey(): string {
    return JSON.stringify([
      this.versionSourceContext,
      this.version?.id,
      this.version?.workflowCode,
      this.version?.definitionVersionCode,
      this.version?.canonicalContentHash,
      this.actorKey(),
    ]);
  }
  private versionKey(): string {
    return JSON.stringify([this.versionSourceKey(), this.versionOwnerContext]);
  }
  private versionCurrent(
    view?: WorkflowVersionActionView | null,
    native = true,
    background: HTMLElement | null = null,
  ): boolean {
    if (!this.managedVersionActions) return true;
    const host = this.element.nativeElement;
    if (
      !view ||
      !this.version ||
      this.destroyRef.destroyed ||
      view.context !== this.versionKey() ||
      view.generation !== this.versionGeneration ||
      !this.versionSourceContext ||
      this.currentVersionSourceContext?.() !== this.versionSourceContext ||
      (this.currentVersionOwnerContext &&
        this.currentVersionOwnerContext() !== this.versionOwnerContext) ||
      this.interactionBlocked ||
      !host.isConnected ||
      host.closest('[hidden],[inert]') ||
      this.dialogs.openDialogs.length ||
      (native && host.closest('[aria-hidden="true"]'))
    )
      return false;
    for (let node: HTMLElement | null = host; node; node = node.parentElement) {
      if (
        !native &&
        node.getAttribute('aria-hidden') === 'true' &&
        (node !== background || node.parentElement !== host.ownerDocument.body)
      )
        return false;
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return false;
    }
    return true;
  }
  private captureVersionAction(
    view: WorkflowVersionActionView | null | undefined,
    permission: OwnedVersionAction['permission'],
  ): OwnedVersionAction | null {
    if (!this.managedVersionActions || !view) return null;
    const host = this.element.nativeElement;
    let background: HTMLElement | null = null;
    for (
      let node: HTMLElement | null = host;
      node && node !== host.ownerDocument.body;
      node = node.parentElement
    )
      if (node.parentElement === host.ownerDocument.body) background = node;
    return { view, actor: this.actorKey(), permission, background };
  }
  private versionActionCurrent(owner: OwnedVersionAction | null): boolean {
    if (!owner) return !this.managedVersionActions;
    return (
      this.managedVersionActions &&
      this.versionCurrent(owner.view, false, owner.background) &&
      owner.actor === this.actorKey() &&
      (owner.permission === 'activate'
        ? this.canActivate()
        : owner.permission === 'rollback'
          ? this.canRollback()
          : this.canRetire())
    );
  }
  private currentActivation(
    activation: WorkflowDefinitionActivationDetail,
  ): boolean {
    const matches = this.activations().filter(
      (row) => row.id === activation.id,
    );
    return (
      !this.activationsLoadFailed() &&
      !this.isLoadingActivations() &&
      matches.length === 1 &&
      matches[0] === activation &&
      activation.workflowDefinitionVersionId === this.version?.id
    );
  }
  private versionFormCurrent(
    form: HTMLFormElement | undefined,
    action: 'activate' | 'rollback' | 'retire',
    activation?: WorkflowDefinitionActivationDetail,
    owner: OwnedVersionAction | null = null,
  ): boolean {
    if (!this.managedVersionActions) return true;
    const host = this.element.nativeElement;
    const id =
      action === 'retire'
        ? `workflow-retire-${activation?.id}`
        : action === 'activate'
          ? 'workflow-activation-schedule'
          : 'workflow-rollback-schedule';
    const forms = Array.from(
      host.querySelectorAll<HTMLFormElement>('form'),
    ).filter((candidate) => candidate.id === id);
    if (
      !form ||
      !form.isConnected ||
      !host.contains(form) ||
      forms.length !== 1 ||
      forms[0] !== form ||
      (action === 'retire'
        ? this.retiringActivationId() !== activation?.id
        : this.versionAction() !== action)
    )
      return false;
    for (let node: HTMLElement | null = form; node; node = node.parentElement) {
      if (
        node.hidden ||
        node.hasAttribute('inert') ||
        (node.tagName === 'DETAILS' && !node.hasAttribute('open')) ||
        (node.getAttribute('aria-hidden') === 'true' &&
          node !== owner?.background)
      )
        return false;
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return false;
    }
    return true;
  }
  private readVersionCurrent(view: WorkflowVersionActionView | null): boolean {
    return !this.managedVersionActions || this.versionCurrent(view);
  }
  private publicationValues() {
    return {
      displayName: this.publishDisplayName.trim() || null,
      reasonCode: this.note(
        this.publishReasonCode,
        'Published from workflow editor.',
      ),
    };
  }

  private note(value: string, fallback: string): string {
    return value.trim() || fallback;
  }

  private sameValues(
    left: Record<string, string | null>,
    right: Record<string, string | null>,
  ): boolean {
    return Object.keys(left).every((key) => left[key] === right[key]);
  }

  private async confirmAction(
    request: WorkflowDraftConfirmationRequest,
  ): Promise<boolean> {
    if (this.isConfirming()) return false;
    this.isConfirming.set(true);
    try {
      return await this.confirmation.confirm(request);
    } finally {
      this.isConfirming.set(false);
    }
  }

  private onActivationSucceeded(
    result: WorkflowDefinitionActivationDetail,
    message: string,
  ): void {
    this.onVersionMutationSucceeded(
      result.isExecutable === true
        ? 'Available for new process runs.'
        : result.isExecutable === false
          ? 'Activation saved, but this version is not currently available for new process runs. Review its availability checks.'
          : message,
    );
  }

  private async runAction<T>(
    kind: LifecycleActionKind,
    targetId: number,
    targetETag: string | null,
    values: Record<string, string | null>,
    operation: () => Promise<T>,
    onSuccess: (result: T) => void,
    owner: OwnedVersionAction | null = null,
  ): Promise<void> {
    if (kind !== 'publish' && !this.versionActionCurrent(owner)) return;
    if (kind !== 'publish' && this.managedVersionActions) {
      const key = this.versionIntentKey(kind, targetId, targetETag, values);
      if (key !== this.managedIntentKey) this.actionIntent = null;
      this.managedIntentKey = key;
    }
    const request = ++this.mutationRequest;
    this.clearMessages();
    this.isBusy.set(true);
    let submittedIntent = this.actionIntent;
    try {
      const pending = operation();
      submittedIntent = this.actionIntent;
      const result = await pending;
      if (kind !== 'publish' && !this.versionActionCurrent(owner)) return;
      if (kind === 'publish' || this.actionIntent === submittedIntent) {
        this.actionIntent = null;
        if (kind !== 'publish' && this.managedVersionActions)
          this.managedIntentKey = null;
        this.clearPendingOperation();
      }
      onSuccess(result);
    } catch (error) {
      if (kind !== 'publish' && !this.versionActionCurrent(owner)) return;
      const displayError = mapWorkflowRegistryDiagnosticError(error);
      if (
        kind !== 'publish' &&
        this.managedVersionActions &&
        (displayError.statusCode === 409 || displayError.statusCode === 412)
      ) {
        if (kind === 'retire') this.activationsLoadFailed.set(true);
        else this.versionChangeFailed.set(true);
      }
      if (
        (kind === 'retire' || kind === 'rollback') &&
        this.isAssuranceFailure(
          displayError.issueCodes,
          displayError.reasonCode,
        )
      ) {
        this.persistPendingOperation(kind, targetId, targetETag, values);
        if (this.auth.startAssuranceStepUp(this.router.url)) {
          this.statusMessage.set(
            'Identity verification is starting. Return here to review and confirm the action again.',
          );
        } else {
          this.errorTitle.set(
            'Trusted identity verification is not configured',
          );
          this.errorMessages.set([
            "This high-risk action remains blocked. Configure the deployment's browser assurance step-up endpoint, trusted provider claim, and authentication age before retrying.",
          ]);
        }
        return;
      }

      this.showActionFailure(kind, displayError);
      if (
        kind === 'publish' &&
        [
          displayError.problemCode,
          displayError.reasonCode,
          ...(displayError.issueCodes ?? []),
        ].includes('workflow.definition.draft.archived')
      ) {
        this.actionIntent = null;
        this.clearPendingOperation();
        this.publicationChecksFailed.emit(displayError);
      }
    } finally {
      if (request === this.mutationRequest) this.isBusy.set(false);
    }
  }
  private versionIntentKey(
    kind: LifecycleActionKind,
    targetId: number,
    targetETag: string | null,
    values: Record<string, string | null>,
  ): string {
    return JSON.stringify([
      this.versionSourceKey(),
      kind,
      targetId,
      targetETag,
      Object.keys(values)
        .sort()
        .map((key) => [key, values[key]]),
    ]);
  }
  private intent(): WorkflowDefinitionMutationIntent {
    this.actionIntent ??= createWorkflowDefinitionMutationIntent();
    return this.actionIntent;
  }

  private showActionFailure(
    kind: LifecycleActionKind,
    error: WorkflowRegistryDiagnosticDisplayError,
  ): void {
    this.errorReference.set(error.correlationId || null);
    if (this.hasUnconfirmedOutcome(error)) {
      const action = {
        publish: 'Publication',
        activate: 'Activation',
        retire: 'Retirement',
        rollback: 'Rollback',
        make_current: 'Version update',
      }[kind];
      const nextStep =
        kind === 'publish'
          ? 'Check Published versions before trying again.'
          : 'Check this version’s availability and activation history before trying again.';
      this.errorTitle.set(`${action} could not be confirmed`);
      this.errorMessages.set([
        `The server did not confirm the result of this action. ${nextStep}`,
        'If the problem continues, contact your administrator and share the support reference if one is shown.',
      ]);
      return;
    }

    this.errorTitle.set(error.title);
    this.errorMessages.set(error.messages);
  }

  private hasUnconfirmedOutcome(
    error: WorkflowRegistryDiagnosticDisplayError,
  ): boolean {
    return (
      !error.statusCode || error.statusCode < 400 || error.statusCode >= 500
    );
  }

  private onVersionMutationSucceeded(message: string): void {
    this.statusMessage.set(message);
    this.loadActivations();
    this.loadVersionChangePreview();
    this.roles.reload();
    this.versionChanged.emit();
  }

  private loadVersionChangePreview(): void {
    const view = this.versionView;
    if (!this.readVersionCurrent(view)) return;
    const version = this.version;
    const requestId = ++this.versionChangeRequestId;
    if (!version) return;
    this.isLoadingVersionChange.set(true);
    this.versionChangeFailed.set(false);
    this.lifecycle
      .getVersionChangePreview(version.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview) => {
          if (requestId !== this.versionChangeRequestId) return;
          if (!this.readVersionCurrent(view)) {
            this.isLoadingVersionChange.set(false);
            this.versionChangeFailed.set(true);
            return;
          }
          if (preview.definitionVersionId !== this.version?.id) {
            this.isLoadingVersionChange.set(false);
            this.versionChangeFailed.set(true);
            return;
          }
          this.versionChange.set(preview);
          this.isLoadingVersionChange.set(false);
        },
        error: () => {
          if (requestId !== this.versionChangeRequestId) return;
          if (!this.readVersionCurrent(view)) {
            this.isLoadingVersionChange.set(false);
            this.versionChangeFailed.set(true);
            return;
          }
          this.isLoadingVersionChange.set(false);
          this.versionChangeFailed.set(true);
        },
      });
  }

  private loadActivations(): void {
    const view = this.versionView;
    if (!this.readVersionCurrent(view)) return;
    const version = this.version;
    const requestId = ++this.activationRequestId;
    if (!version) {
      this.isLoadingActivations.set(false);
      return;
    }

    this.isLoadingActivations.set(true);
    this.activationsLoadFailed.set(false);
    this.lifecycle
      .getActivations(version.id, {
        pageNumber: 1,
        pageSize: 100,
        includeRetired: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          if (requestId === this.activationRequestId) {
            if (!this.readVersionCurrent(view)) {
              this.isLoadingActivations.set(false);
              this.activationsLoadFailed.set(true);
              return;
            }
            if (
              this.managedVersionActions &&
              page.definitionVersionId !== this.version?.id
            ) {
              this.activationsLoadFailed.set(true);
              this.isLoadingActivations.set(false);
              return;
            }
            const historyError = this.activationHistoryError;
            if (
              historyError &&
              historyError.source === this.versionSourceKey() &&
              this.errorTitle() === historyError.title &&
              this.errorMessages() === historyError.messages &&
              this.errorReference() === historyError.reference
            ) {
              this.errorTitle.set(null);
              this.errorMessages.set([]);
              this.errorReference.set(null);
            }
            this.activationHistoryError = null;
            this.activations.set(page.items ?? []);
            this.activationTotalCount.set(page.totalCount);
            this.isLoadingActivations.set(false);
          }
        },
        error: (error: unknown) => {
          if (requestId === this.activationRequestId) {
            if (!this.readVersionCurrent(view)) {
              this.isLoadingActivations.set(false);
              this.activationsLoadFailed.set(true);
              return;
            }
            const displayError = mapWorkflowRegistryDiagnosticError(error);
            this.errorTitle.set('Activation history could not be loaded');
            this.errorMessages.set(
              this.hasUnconfirmedOutcome(displayError)
                ? [
                    'Retry history to check saved activation records. This does not activate the workflow or change its availability.',
                  ]
                : displayError.messages,
            );
            this.errorReference.set(displayError.correlationId || null);
            this.activationHistoryError = {
              source: this.versionSourceKey(),
              title: this.errorTitle(),
              messages: this.errorMessages(),
              reference: this.errorReference(),
            };
            this.activationsLoadFailed.set(true);
            this.isLoadingActivations.set(false);
          }
        },
      });
  }
  private persistPendingOperation(
    kind: LifecycleActionKind,
    targetId: number,
    targetETag: string | null,
    values: Record<string, string | null>,
  ): void {
    const actorId = this.roles.accessProfile()?.actor.actorId;
    if (!actorId) {
      return;
    }
    const pending: PendingLifecycleOperation = {
      kind,
      actorId,
      targetId,
      targetETag,
      idempotencyKey: this.intent().idempotencyKey,
      values,
      expiresUtc: new Date(
        Date.now() + PENDING_OPERATION_LIFETIME_MS,
      ).toISOString(),
      ...(kind !== 'publish' && this.managedVersionActions
        ? { sourceContext: this.versionSourceContext }
        : {}),
    };
    window.sessionStorage.setItem(
      PENDING_OPERATION_STORAGE_KEY,
      JSON.stringify(pending),
    );
  }

  private restorePendingOperation(result: 'satisfied' | 'failed'): void {
    const pending = this.readPendingOperation();
    if (!pending) {
      return;
    }
    if (
      pending.kind !== 'publish' &&
      this.managedVersionActions &&
      !this.versionCurrent(this.versionView)
    )
      return;
    this.clearPendingOperation();
    if (result === 'failed') {
      this.errorTitle.set('Identity verification was not satisfied');
      this.errorMessages.set([
        'The action was not performed. Verify the required identity context and try again.',
      ]);
      return;
    }

    const actorId = this.roles.accessProfile()?.actor.actorId;
    const expectedTargetId =
      pending.kind === 'publish' ? this.draft?.id : this.version?.id;
    if (
      !actorId ||
      actorId !== pending.actorId ||
      (pending.kind !== 'publish' &&
        this.managedVersionActions &&
        !!pending.sourceContext &&
        pending.sourceContext !== this.versionSourceContext) ||
      (pending.kind !== 'retire' && expectedTargetId !== pending.targetId)
    ) {
      this.errorTitle.set('Pending action no longer matches this workspace');
      this.errorMessages.set([
        'The action was not performed. Reopen the intended draft or version and review it again.',
      ]);
      return;
    }

    this.actionIntent = { idempotencyKey: pending.idempotencyKey };
    if (pending.kind !== 'publish' && this.managedVersionActions)
      this.managedIntentKey = this.versionIntentKey(
        pending.kind,
        pending.targetId,
        pending.targetETag,
        pending.values,
      );
    this.restoreValues(pending);
    this.statusMessage.set(
      'Identity verified by the server. Review the restored values, then select the action again to confirm it.',
    );
  }
  private restoreValues(pending: PendingLifecycleOperation): void {
    const value = (key: string) => pending.values[key] ?? '';
    switch (pending.kind) {
      case 'publish':
        this.publishDisplayName = value('displayName');
        this.publishReasonCode = value('reasonCode');
        break;
      case 'activate':
        this.versionAction.set(
          pending.values['effectiveStartUtc'] ? 'activate' : null,
        );
        this.restoreScheduleField(
          'activationStart',
          value('effectiveStartUtc'),
        );
        this.restoreScheduleField('activationEnd', value('effectiveEndUtc'));
        this.activationReasonCode = value('reasonCode');
        break;
      case 'make_current':
        this.activationReasonCode = value('reasonCode');
        this.loadVersionChangePreview();
        break;
      case 'rollback':
        this.versionAction.set('rollback');
        this.restoreScheduleField('rollbackStart', value('effectiveStartUtc'));
        this.restoreScheduleField('rollbackEnd', value('effectiveEndUtc'));
        this.rollbackReasonCode = value('reasonCode');
        break;
      case 'retire':
        this.retiringActivationId.set(pending.targetId);
        this.retireReasonCodes[pending.targetId] = value('reasonCode');
        break;
    }
  }

  private readPendingOperation(): PendingLifecycleOperation | null {
    const raw = window.sessionStorage.getItem(PENDING_OPERATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as PendingLifecycleOperation;
      if (
        !parsed.actorId ||
        !parsed.idempotencyKey ||
        !parsed.kind ||
        !Number.isFinite(parsed.targetId) ||
        new Date(parsed.expiresUtc).getTime() <= Date.now()
      ) {
        this.clearPendingOperation();
        return null;
      }
      return parsed;
    } catch {
      this.clearPendingOperation();
      return null;
    }
  }

  private clearPendingOperation(): void {
    window.sessionStorage.removeItem(PENDING_OPERATION_STORAGE_KEY);
  }

  private clearMessages(): void {
    this.activationHistoryError = null;
    this.errorTitle.set(null);
    this.errorMessages.set([]);
    this.errorReference.set(null);
    this.statusMessage.set(null);
  }

  private isAssuranceFailure(
    issueCodes: readonly string[] | undefined,
    reasonCode?: string,
  ): boolean {
    return [...(issueCodes ?? []), reasonCode ?? ''].some(
      (code) =>
        code === 'auth.assurance_required' || code === 'auth.assurance_expired',
    );
  }

  private toLocalInput(value: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, -1);
  }

  private restoreScheduleField(field: ScheduleField, utc: string): void {
    this[field] = this.toLocalInput(utc);
    this.restoredInstants[field] = { local: this[field], utc };
  }

  private restoredInstant(field: ScheduleField): string | undefined {
    const restored = this.restoredInstants[field];
    return restored?.local === this[field] ? restored.utc : undefined;
  }
}
